import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { FiInbox, FiLink } from "react-icons/fi";
import { LuUnlink } from "react-icons/lu";
import { Link as RouterLink } from "react-router-dom";

const SETTINGS_PATH = "/settings/organization";

export type MetaAdsConnectionEmptyReason = "needs_login" | "no_ad_accounts";

type MetaAdsConnectionEmptyStateProps = {
  reason: MetaAdsConnectionEmptyReason;
  /** When set (needs_login + Meta config present), primary CTA runs OAuth instead of navigating. */
  onConnectMetaAds?: () => void;
  isConnectMetaAdsLoading?: boolean;
};

const copy: Record<
  MetaAdsConnectionEmptyReason,
  {
    statusLabel: string;
    title: string;
    description: string;
    primaryCta: string;
    showSecondaryLink: boolean;
    StatusIcon: IconType;
  }
> = {
  needs_login: {
    statusLabel: "Not connected",
    title: "Connect your Meta Ads account",
    description:
      "Link your Meta Ads Manager to create and manage campaigns directly from KYD",
    primaryCta: "Connect Meta Ads",
    showSecondaryLink: true,
    StatusIcon: LuUnlink,
  },
  no_ad_accounts: {
    statusLabel: "No ad accounts",
    title: "No ad accounts available",
    description:
      "Your organization is linked to Meta, but we don't see any ad accounts yet. Open settings to review your Meta connection and permissions.",
    primaryCta: "Open settings",
    showSecondaryLink: false,
    StatusIcon: FiInbox,
  },
};

/**
 * Empty state when Meta is not ready for org-level campaigns (Figma: meta campaign blocker).
 */
export function MetaAdsConnectionEmptyState({
  reason,
  onConnectMetaAds,
  isConnectMetaAdsLoading = false,
}: MetaAdsConnectionEmptyStateProps) {
  const c = copy[reason];
  const StatusIcon = c.StatusIcon;
  const useInlineMetaConnect =
    reason === "needs_login" && typeof onConnectMetaAds === "function";

  return (
    <VStack w="100%" align="stretch" spacing={0}>
      <HStack
        justify="space-between"
        align="flex-start"
        w="100%"
        py={3}
        borderBottomWidth="1px"
        borderColor="gray.300"
        spacing={4}
      >
        <VStack align="flex-start" spacing={0.5} pb={1.5} flex="1" minW={0}>
          <Text
            fontSize="2xl"
            fontWeight="semibold"
            color="gray.800"
            lineHeight="1.2"
            letterSpacing="-0.03em"
          >
            Meta Campaigns
          </Text>
        </VStack>
        <HStack
          spacing={2.5}
          pl={4}
          pr={2}
          py={2.5}
          borderRadius="md"
          flexShrink={0}
        >
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="gray.500"
            lineHeight="none"
          >
            {c.statusLabel}
          </Text>
          <Icon as={StatusIcon} boxSize={6} color="gray.500" aria-hidden />
        </HStack>
      </HStack>

      <Flex
        justify="center"
        w="100%"
        py={{ base: 10, md: 12 }}
        px={{ base: 4, md: 8 }}
      >
        <Box
          w="full"
          maxW="400px"
          bg="#fbfbfb"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="2xl"
          boxShadow="0px 4px 10px 0px rgba(0, 0, 0, 0.05)"
          p={6}
        >
          <VStack spacing={4} align="stretch" w="100%">
            <VStack spacing={1.5} align="center" textAlign="center" w="100%">
              <Icon as={FiLink} boxSize={8} color="blue.500" aria-hidden />
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color="gray.800"
                lineHeight="1.3"
              >
                {c.title}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color="gray.600"
                lineHeight="1.3"
                maxW="312px"
              >
                {c.description}
              </Text>
            </VStack>

            <VStack spacing={2.5} align="stretch" w="100%">
              {useInlineMetaConnect ? (
                <Button
                  onClick={onConnectMetaAds}
                  isLoading={isConnectMetaAdsLoading}
                  isDisabled={isConnectMetaAdsLoading}
                  w="100%"
                  bg="gray.800"
                  color="white"
                  fontWeight="semibold"
                  fontSize="sm"
                  lineHeight="1.4"
                  py={2.5}
                  px={6}
                  borderRadius="md"
                  boxShadow="0px 10px 15px 0px #f7f7f7"
                  _hover={{ bg: "gray.700" }}
                  _active={{ bg: "gray.700" }}
                >
                  {c.primaryCta}
                </Button>
              ) : (
                <Button
                  as={RouterLink}
                  to={SETTINGS_PATH}
                  w="100%"
                  bg="gray.800"
                  color="white"
                  fontWeight="semibold"
                  fontSize="sm"
                  lineHeight="1.4"
                  py={2.5}
                  px={6}
                  borderRadius="md"
                  boxShadow="0px 10px 15px 0px #f7f7f7"
                  _hover={{ bg: "gray.700" }}
                  _active={{ bg: "gray.700" }}
                >
                  {c.primaryCta}
                </Button>
              )}
              {c.showSecondaryLink ? (
                <Flex justify="center" pt={2.5} px={3}>
                  <Link
                    as={RouterLink}
                    to={SETTINGS_PATH}
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.800"
                    textDecoration="underline"
                    lineHeight="1.2"
                  >
                    Go to settings
                  </Link>
                </Flex>
              ) : null}
            </VStack>
          </VStack>
        </Box>
      </Flex>
    </VStack>
  );
}
